/**
 * The selected unit, in one card (docs/ux.md U-CTX-01, UI v3): its flag, name, side and cell;
 * the watercolour; what it is now (holes, shots left, sailing, repair, squadron); what its class
 * is (guns, shots, range, speeds, when it sinks); and its commands. The same card for an own
 * ship, an enemy and a fort, on every screen: a card at the right on desktops; on phones a
 * bar at the bottom that opens into a sheet. All of it comes from `inspectorModel`.
 */
import type { Action } from '@brig/rules-engine';
import { useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { NationFlag } from '../nations/NationFlag.tsx';
import { cardPlaceholder, cardUrl, effectiveArt, SHIP_ART } from '../ships/art.ts';
import { ShipToken } from '../ships/ShipToken.tsx';
import type { Ability } from './abilities.ts';
import type { Hints } from './hints.ts';
import { current } from './history.ts';
import { HudButton } from './HudButton.tsx';
import { Icon, type IconName } from './icons.tsx';
import { inspectorModel, type InspectorModel, type MoveStatus } from './inspector.ts';
import { kindOf, ownName, unitLabel } from './names.ts';
import { uiPhase } from './phase.ts';
import * as css from './ShipInspector.css.ts';
import { squadronCandidates } from './turn.ts';
import { liveSquadron } from './turnMachine.ts';
import type { TurnSend, TurnSnapshot } from './useTurn.ts';
import { whyText } from './why.ts';

/** How far the bar must be pulled up (or the sheet down) to open (or fold), CSS px. */
const SWIPE_PX = 40;

export interface ShipInspectorProps {
  readonly snapshot: TurnSnapshot;
  readonly send: TurnSend;
  readonly id: string;
  readonly hints: Hints;
  /**
   * Everything shown (the card, the sheet), or only the head and the commands (the phone's bar).
   * Phones toggle it; desktops always show the whole card.
   */
  readonly expanded: boolean;
  /** Phones: the card opens and folds; without it there is no such button. */
  readonly onExpanded?: ((v: boolean) => void) | undefined;
  /** × only puts the card away, the unit stays selected. Without it, × deselects. */
  readonly onClose?: (() => void) | undefined;
  readonly onApply: (action: Action) => void;
  readonly onCentre: () => void;
}

export function ShipInspector({
  snapshot,
  send,
  id,
  hints,
  expanded,
  onExpanded,
  onClose,
  onApply,
  onCentre,
}: ShipInspectorProps) {
  const { t } = useTranslation();
  const state = current(snapshot.context.history);
  const phase = uiPhase(snapshot);
  const playing = phase === 'muster' || phase === 'battle';
  const mustering = phase === 'muster';
  const model = inspectorModel(state, id, hints, playing, mustering ? liveSquadron(snapshot.context) : null);
  const drag = useRef<{ y: number; dy: number } | null>(null);
  if (!model) return null;
  const nations = snapshot.context.history.nations;
  const name = unitLabel(t, state, nations, id);
  // Its own name (Palomo, Redoutable, Morro…), from its nation's list, under what it is.
  const own = ownName(state, nations, id);
  const why = (a: Ability) =>
    a.ok ? '' : a.reason ? whyText(t, state, a.reason) : a.note ? t(`game.unitPanel.note.${a.note}`) : '';
  const fire = snapshot.context.fire;
  const ship = state.ships.find((s) => s.id === id);

  const actions = commands();
  const toggle = onExpanded
    ? () => {
        onExpanded(!expanded);
      }
    : null;

  return (
    <section
      className={css.inspector}
      data-side={model.own ? 'own' : 'enemy'}
      data-expanded={expanded}
      data-foldable={toggle !== null}
      aria-label={name}
      data-testid="inspector"
    >
      <header
        className={css.head}
        onPointerDown={(e) => {
          if (!toggle || (e.target instanceof Element && e.target.closest('button'))) return;
          drag.current = { y: e.clientY, dy: 0 };
          e.currentTarget.setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => {
          if (drag.current) drag.current.dy = e.clientY - drag.current.y;
        }}
        onPointerUp={() => {
          // Only a press that began on the bare head: its buttons answer their own clicks.
          if (!drag.current || !onExpanded) return;
          const dy = drag.current.dy;
          drag.current = null;
          // U-TOUCH-03: up opens the card, down folds it; a tap on the bare head toggles it.
          if (dy < -SWIPE_PX) onExpanded(true);
          else if (dy > SWIPE_PX) onExpanded(false);
          else if (Math.abs(dy) < 6) onExpanded(!expanded);
        }}
        onPointerCancel={() => {
          drag.current = null;
        }}
      >
        {toggle ? <span className={css.grip} aria-hidden="true" /> : null}
        <span className={css.thumb} aria-hidden="true">
          {ship ? (
            <ShipToken type={ship.cls} nation={nations[ship.side]} size={40} />
          ) : (
            <Icon name="fort" size={24} />
          )}
        </span>
        <span className={css.title}>
          <h2 className={css.name}>
            <NationFlag nation={nations[model.side]} height={14} decorative />
            <span>{own ? kindOf(t, state, id) : name}</span>
          </h2>
          <span className={css.sub}>
            {own ? (
              <>
                <span className={css.ownName} data-testid="inspector-own-name">
                  {own}
                </span>
                {' · '}
              </>
            ) : null}
            {state.players[model.side].name} · {model.cell}
            {model.own ? null : <span className={css.srOnly}> · {t('game.unitPanel.enemy')}</span>}
          </span>
        </span>
        <span className={css.mini} aria-hidden="true">
          <Segments n={model.damage.n} max={model.damage.max} tone="hole" />
          {model.gone || !model.own ? null : <Pips n={model.shots.left} max={model.shots.max} />}
        </span>
        {toggle ? (
          <HudButton
            icon={expanded ? 'chevronDown' : 'chevronUp'}
            size="sm"
            label={expanded ? t('game.inspector.collapse') : t('game.inspector.expand')}
            expanded={expanded}
            onClick={toggle}
          />
        ) : null}
        <HudButton
          icon="close"
          size="sm"
          label={onClose ? t('game.inspector.close') : t('game.unitPanel.deselect')}
          keyHint="Esc"
          testId="inspector-close"
          onClick={() => {
            if (onClose) onClose();
            else send({ type: 'SELECT', id: null });
          }}
        />
      </header>

      <div className={css.body}>
        <Art model={model} nation={nations[model.side]} alt={t('ships.card.alt', { ship: name })} />
        <Status model={model} why={why} moveText={moveText} moveWhy={moveWhy} />
        {model.specs ? <Specs model={model} /> : null}
      </div>

      {actions.length > 0 ? <footer className={css.actions}>{actions}</footer> : null}
    </section>
  );

  /** The commands the engine allows this unit now; the others dimmed with the reason. */
  function commands(): ReactNode[] {
    if (!model) return [];
    const out: ReactNode[] = [];
    const centre = (
      <HudButton key="centre" icon="centre" label={t('game.unitPanel.centre')} onClick={onCentre} />
    );
    if (model.own && model.fire) {
      const shoot = model.fire;
      if (model.move) {
        const move = model.move;
        const can = move.kind === 'available';
        out.push(
          <HudButton
            key="move"
            icon="sail"
            label={t('game.unitPanel.move')}
            tone="accent"
            disabled={!can}
            why={moveWhy(move)}
            pressed={can && !fire && !snapshot.context.portMove}
            testId="inspector-move"
            onClick={() => {
              if (fire) send({ type: 'CLOSE_FIRE' });
              if (snapshot.context.portMove) send({ type: 'PORT_MOVE' });
              document.querySelector<HTMLElement>('[data-testid="board"] [role="application"]')?.focus();
            }}
          />,
        );
      }
      out.push(
        <HudButton
          key="fire"
          icon="cannon"
          label={t('game.unitPanel.fire')}
          keyHint="F"
          tone="danger"
          disabled={!shoot.ok}
          why={why(shoot)}
          pressed={fire !== null}
          testId="inspector-fire"
          onClick={() => {
            send({ type: fire ? 'CLOSE_FIRE' : 'NEXT_TARGET' });
          }}
        />,
      );
      if (mustering && ship && squadronCandidates(state).includes(id)) {
        const inSquadron = model.squadron === 'in';
        out.push(
          <HudButton
            key="squadron"
            icon="flag"
            label={inSquadron ? t('game.action.removeFromSquadron') : t('game.action.addToSquadron')}
            keyHint="S"
            pressed={inSquadron}
            onClick={() => {
              send({ type: 'TOGGLE', id });
            }}
          />,
        );
      }
      if (hints.portCells.size > 0) {
        out.push(
          <HudButton
            key="port"
            icon="anchor"
            label={snapshot.context.portMove ? t('game.action.portMoveCancel') : t('game.action.portMove')}
            note={t('game.action.portMoveHint')}
            pressed={snapshot.context.portMove}
            onClick={() => {
              send({ type: 'PORT_MOVE' });
            }}
          />,
        );
      }
      const extra: [string, IconName, string, Action | null][] = [
        ['dock', 'wrench', t('game.unitPanel.dock'), hints.dock],
        [
          'repair',
          'hammer',
          t('game.unitPanel.fortRepair', { n: state.ruleset.fortRepairCrew }),
          hints.startFortRepair,
        ],
        ['stop', 'stop', t('game.unitPanel.fortRepairStop'), hints.stopFortRepair],
      ];
      for (const [key, icon, label, action] of extra) {
        if (action) {
          out.push(
            <HudButton
              key={key}
              icon={icon}
              label={label}
              onClick={() => {
                onApply(action);
              }}
            />,
          );
        }
      }
    } else if (!model.own && model.shooters !== null) {
      // U-CTX-04: an enemy can only be fired at, by whoever reaches it.
      const reach = model.shooters;
      out.push(
        <HudButton
          key="aim"
          icon="cannon"
          label={t('game.unitPanel.fireAt')}
          tone="danger"
          disabled={reach === 0}
          why={t('game.unitPanel.outOfReach')}
          note={reach > 0 ? t('game.unitPanel.shooters', { n: reach }) : undefined}
          pressed={fire?.target === id}
          badge={reach > 0 ? reach : undefined}
          testId="inspector-fire"
          onClick={() => {
            send(fire?.target === id ? { type: 'CLOSE_FIRE' } : { type: 'AIM', target: id });
          }}
        />,
      );
    }
    if (!model.gone) out.push(centre);
    return out;
  }

  /** The sailing row: what the ship can do, or why not, in two or three words. */
  function moveText(move: MoveStatus): string {
    switch (move.kind) {
      case 'available':
        return t('game.inspector.moveAvailable', { n: move.reach });
      case 'done':
        return t('game.inspector.moveDone');
      case 'repair':
        return t('game.inspector.moveRepair');
      case 'weather':
        return move.reason === 'stormDrifted'
          ? t('game.inspector.moveStorm')
          : t('game.inspector.moveBecalmed');
      case 'phase':
        if (outOfSquadron(move)) return t('game.inspector.moveNoSquadron');
        return move.note === 'afterWind' ? t('game.inspector.moveAfterWind') : t('game.inspector.moveNotNow');
      case 'blocked':
        return t('game.inspector.moveBlocked');
    }
  }

  /** The whole reason, for the Move button's and the sailing row's tooltips. */
  function moveWhy(move: MoveStatus): string {
    switch (move.kind) {
      case 'available':
        return '';
      case 'done':
        return whyText(t, state, 'alreadyMoved');
      default:
        return move.reason
          ? whyText(t, state, move.reason)
          : 'note' in move
            ? t(`game.unitPanel.note.${move.note}`)
            : '';
    }
  }
}

/** The ship's watercolour, whole, at its own 16:9; a large token without the artwork; a fort's sign. */
function Art({ model, nation, alt }: { model: InspectorModel; nation: string; alt: string }) {
  const cls = model.specs?.cls;
  if (!cls) {
    return (
      <figure className={css.art} data-kind="fort">
        <Icon name="fort" size={72} />
      </figure>
    );
  }
  if (effectiveArt(SHIP_ART) !== 'source') {
    return (
      <figure className={css.art} data-kind="token">
        <ShipToken type={cls} nation={nation} size={150} art="fallback" />
      </figure>
    );
  }
  return (
    <figure className={css.art} style={{ backgroundImage: `url(${cardPlaceholder(cls) ?? ''})` }}>
      <picture>
        <source type="image/avif" srcSet={`${cardUrl(cls, 1, 'avif')} 1x, ${cardUrl(cls, 2, 'avif')} 2x`} />
        <img
          src={cardUrl(cls, 1, 'webp')}
          srcSet={`${cardUrl(cls, 1, 'webp')} 1x, ${cardUrl(cls, 2, 'webp')} 2x`}
          alt={alt}
          width={480}
          height={270}
          decoding="async"
          data-gone={model.gone}
        />
      </picture>
    </figure>
  );
}

/** What the unit is now: holes taken, shots left, sailing, repair, the squadron. */
function Status({
  model,
  why,
  moveText,
  moveWhy,
}: {
  model: InspectorModel;
  why: (a: Ability) => string;
  moveText: (move: MoveStatus) => string;
  moveWhy: (move: MoveStatus) => string;
}) {
  const { t } = useTranslation();
  const rows: ReactNode[] = [];
  const holes = model.kind === 'ship' ? t('game.inspector.holes') : t('game.inspector.hits');
  rows.push(
    <Row key="holes" icon="hole" label={holes} tone={model.damage.n > 0 ? 'bad' : undefined}>
      <Segments n={model.damage.n} max={model.damage.max} tone="hole" />
      <span data-testid="inspector-holes">
        {t('game.inspector.holesValue', { n: model.damage.n, max: model.damage.max })}
      </span>
    </Row>,
  );
  if (model.gone) {
    rows.push(
      <Row
        key="gone"
        icon="stop"
        label={model.kind === 'ship' ? t('game.inspector.sunk') : t('game.inspector.destroyed')}
      />,
    );
  } else if (model.own) {
    // The other side's shots are theirs to spend on their own turn: only the own ones count now.
    rows.push(
      <Row
        key="shots"
        icon="cannon"
        label={t('game.inspector.shotsLeft')}
        tone={model.shots.left === 0 ? 'off' : undefined}
        tip={model.fire && !model.fire.ok ? why(model.fire) : undefined}
      >
        <Pips n={model.shots.left} max={model.shots.max} />
        <span data-testid="inspector-shots">
          {t('game.inspector.shotsValue', { n: model.shots.left, max: model.shots.max })}
        </span>
      </Row>,
    );
  }
  if (model.move) {
    const move = model.move;
    rows.push(
      <Row
        key="move"
        icon="sail"
        label={t('game.inspector.sailing')}
        tone={move.kind === 'available' ? 'good' : 'off'}
        tip={moveWhy(move) || undefined}
      >
        <span data-testid="inspector-move-status" data-move={move.kind}>
          {moveText(move)}
        </span>
      </Row>,
    );
  }
  if (model.repair) {
    const r = model.repair;
    rows.push(
      <Row
        key="repair"
        icon={r.kind === 'dock' ? 'wrench' : 'hammer'}
        label={t('game.inspector.repair')}
        tone="off"
      >
        <span>
          {r.kind === 'dock'
            ? t('game.inspector.repairDock', { n: r.turns })
            : t('game.inspector.repairCrew', { fort: r.fortId })}
        </span>
      </Row>,
    );
  }
  // Said once: when not being in the squadron is why she can't sail, the sailing row says so.
  if (model.squadron && !(model.move && outOfSquadron(model.move))) {
    rows.push(
      <Row
        key="squadron"
        icon="flag"
        label={t('game.inspector.squadron')}
        tone={model.squadron === 'in' ? 'good' : 'off'}
      >
        <span data-testid="inspector-squadron">
          {model.squadron === 'in' ? t('game.squadron.in') : t('game.squadron.out')}
        </span>
      </Row>,
    );
  }
  return (
    <section className={css.status} aria-label={t('game.inspector.state')}>
      <h3 className={css.sectionTitle}>{t('game.inspector.state')}</h3>
      <dl className={css.rows}>{rows}</dl>
    </section>
  );
}

/** A class's fixed numbers (R-FLEET-01), in two columns. */
function Specs({ model }: { model: InspectorModel }) {
  const { t } = useTranslation();
  const s = model.specs;
  if (!s) return null;
  const cells = (n: number) => t('game.inspector.cells', { n });
  const items: [IconName, string, string][] = [
    ['cannon', t('game.inspector.guns'), t(`ships.guns.${s.cls}`)],
    ['volley', t('game.inspector.shotsPerTurn'), String(s.shotsPerTurn)],
    ['range', t('game.inspector.range'), cells(s.range)],
    ['wind', t('game.inspector.fresh'), String(s.freshSpeed)],
    ['calm', t('game.inspector.calm'), s.calmSpeed ? String(s.calmSpeed) : t('game.inspector.noCalm')],
    ['storm', t('game.inspector.storm'), String(s.stormDrift)],
    ['hole', t('game.inspector.sinksAt'), t('game.inspector.sinksAtValue', { n: s.sinksAt })],
  ];
  return (
    <section className={css.specs} aria-label={t('game.inspector.specs')}>
      <h3 className={css.sectionTitle}>{t('game.inspector.specs')}</h3>
      <dl className={css.specGrid} data-testid="inspector-specs">
        {items.map(([icon, label, value]) => (
          <div key={label} className={css.spec} data-wide={icon === 'cannon'}>
            <dt>
              <Icon name={icon} size={15} />
              {label}
            </dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function Row({
  icon,
  label,
  tone,
  tip,
  children,
}: {
  icon: IconName;
  label: string;
  tone?: 'good' | 'bad' | 'off' | undefined;
  tip?: string | undefined;
  children?: ReactNode;
}) {
  return (
    <div className={css.row} data-tone={tone} data-tip={tip || undefined}>
      <dt>
        <Icon name={icon} size={16} />
        {label}
      </dt>
      <dd>{children}</dd>
    </div>
  );
}

/** Whether a ship can't sail only because she is not in the squadron. */
function outOfSquadron(move: MoveStatus): boolean {
  return (
    move.kind === 'phase' &&
    (move.reason === 'notInSquadron' || ('note' in move && move.note === 'chooseToSail'))
  );
}

/** Holes taken: one segment each up to the sinking point; red ones are holes. */
export function Segments({ n, max, tone }: { n: number; max: number; tone: 'hole' }) {
  return (
    <span className={css.segments} data-tone={tone} aria-hidden="true">
      {Array.from({ length: max }, (_, i) => (
        <span key={i} data-on={i < n} />
      ))}
    </span>
  );
}

/** Shots: filled are left this turn, hollow ones spent. */
function Pips({ n, max }: { n: number; max: number }) {
  return (
    <span className={css.pips} aria-hidden="true">
      {Array.from({ length: max }, (_, i) => (
        <span key={i} data-on={i < n} />
      ))}
    </span>
  );
}
